<template>
  <div class="p-4">
    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <div class="flex gap-4">
        <div v-for="i in 3" :key="i" class="shimmerElement w-24 h-24 rounded-xl flex-shrink-0"></div>
      </div>
    </div>

    <div v-else class="flex gap-3 overflow-x-auto pb-2">
      <!-- Carte boissons -->
      <div
        v-for="drink in availableDrinks"
        :key="drink.id"
        class="flex-shrink-0 w-28 rounded-xl border-2 overflow-hidden transition-colors"
        :class="{
          'border-primary bg-primary-50': hasDrink(drink.id),
          'border-gray-200 bg-white': !hasDrink(drink.id)
        }"
      >
        <!-- Image boisson -->
        <div class="relative w-full aspect-square">
          <ImgComponent
            :src="drink.image"
            class="w-full h-full object-cover"
            alt=""
          />
        </div>

        <!-- Info + contrôles -->
        <div class="p-2">
          <div class="text-xs font-semibold truncate">{{ drink.name }}</div>
          <div class="text-xs text-primary font-bold">{{ drink.amount }} FCFA</div>

          <!-- Contrôles quantité -->
          <div class="flex items-center justify-between mt-1">
            <button
              @click="decreaseDrink(drink)"
              :disabled="!hasDrink(drink.id)"
              class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs"
              :class="{ 'opacity-50': !hasDrink(drink.id) }"
            >
              -
            </button>

            <span class="text-xs font-bold">
              {{ getDrinkQuantity(drink.id) }}
            </span>

            <button
              @click="increaseDrink(drink)"
              class="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';

const props = defineProps({
  itemId: {
    type: String,
    required: true
  },
  restaurantId: {
    type: String,
    required: true
  },
  selectedDrinks: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update']);

const loading = ref(true);
const availableDrinks = ref([]);
const currentDrinks = ref([]);

// Charger les boissons du restaurant
onMounted(async () => {
  try {
    const requestId = `fetch-restaurant-drinks-${props.restaurantId}-${Date.now()}`;

    window.bus.emit(window.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      method: 'GET',
      url: window.EVENTS.ENDPOINTS?.RESTAURANTS?.DRINKS?.replace('{restaurantId}', props.restaurantId) || `/api/restaurants/${props.restaurantId}/drinks`,
      data: {}
    });

    // FIX: Use proper cleanup to prevent memory leaks
    const handler = (res) => {
      if (res.id === requestId) {
        if (res.success) {
          availableDrinks.value = res.data || [];
          // Initialiser depuis props
          currentDrinks.value = props.selectedDrinks.map(d => ({
            drinkId: d.drinkId,
            name: d.name,
            quantity: d.quantity,
            price: d.price
          }));
        }
        loading.value = false;
        window.bus.off(window.EVENTS.NETWORK.RESPONSE, handler);
      }
    };

    window.bus.on(window.EVENTS.NETWORK.RESPONSE, handler);
  } catch (error) {
    console.error('Failed to load drinks:', error);
    loading.value = false;
  }
});

const hasDrink = (drinkId) => {
  return currentDrinks.value.some(d => d.drinkId === drinkId && d.quantity > 0);
};

const getDrinkQuantity = (drinkId) => {
  const drink = currentDrinks.value.find(d => d.drinkId === drinkId);
  return drink?.quantity || 0;
};

const increaseDrink = (drink) => {
  const existing = currentDrinks.value.find(d => d.drinkId === drink.id);

  if (existing) {
    existing.quantity++;
  } else {
    currentDrinks.value.push({
      drinkId: drink.id,
      name: drink.name,
      quantity: 1,
      price: drink.amount
    });
  }

  emit('update', currentDrinks.value.filter(d => d.quantity > 0));
};

const decreaseDrink = (drink) => {
  const existing = currentDrinks.value.find(d => d.drinkId === drink.id);

  if (existing) {
    existing.quantity--;
    if (existing.quantity <= 0) {
      currentDrinks.value = currentDrinks.value.filter(d => d.drinkId !== drink.id);
    }
  }

  emit('update', currentDrinks.value.filter(d => d.quantity > 0));
};
</script>

<style scoped>
.shimmerElement {
  animation: shimmer 2s infinite;
  background: linear-gradient(
    to right,
    #f3f4f6 0%,
    #e5e7eb 20%,
    #f3f4f6 40%,
    #f3f4f6 100%
  );
  background-size: 1000px 100%;
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}
</style>
